/**
 * EL VOCABULARIO DE LOS PAQUETES, EN UN SOLO SITIO.
 * =============================================================================
 *
 * Los paquetes se renombraron COMERCIALMENTE (2026-07):
 *
 *   doc-id viejo      doc-id nuevo    nombre visual
 *   ----------------  --------------  -------------
 *   gtr-lite          gtr-go          GTR Go
 *   trading-academy   gtr-fly         GTR Fly
 *   gtr-vip           gtr-win         GTR Win
 *   mmi-colombia      mmi-colombia    (sin cambio)
 *
 * ⚠️ ESTO NO ES UN RENAME: ES ADITIVO. Los ids viejos SIGUEN VIVOS y siguen
 * siendo el caso normal. Razones, y son duras:
 *
 *   · Los ~41.5k usuarios dormidos conservan `users/{u}/subscriptions/gtr-lite`
 *     y `.../trading-academy`. Renombrar el código los volvería invisibles: no
 *     activos, sin historia de primera vez, fuera del corte — en silencio.
 *   · La historia (comisiones, ledger de promociones, BigQuery) también habla el
 *     idioma viejo, y no se reescribe.
 *   · La suite de 806 tests es el testigo independiente de que el comportamiento
 *     con los ids viejos NO cambia. Si algo aquí se comporta distinto con
 *     `trading-academy`, el cambio está mal.
 *
 * Por eso todo lo de aquí son UNIONES (viejo ∪ nuevo), nunca sustituciones.
 *
 * DOS ESPACIOS DE NOMBRES, Y SE CONFUNDEN SOLOS
 * -----------------------------------------------------------------------------
 * El NIVEL (`SubscriptionPackage`) y el DOCUMENTO DE SUSCRIPCIÓN NO son la misma
 * cadena, aunque se escriban parecido. El documento es un doc-id real de
 * `subscriptions/{id}`; el nivel es una etiqueta que el código DERIVA en
 * `getUserPackageStatus` a partir de qué documentos están vigentes. Aquí están
 * separados a propósito: `*_SUBSCRIPTION_IDS` para doc-ids, y la sección "EL
 * VOCABULARIO DE NIVEL" (más abajo) para las etiquetas.
 *
 * QUÉ SE ESCRIBE Y QUÉ SE LEE
 * -----------------------------------------------------------------------------
 * Desde 2026-07-24 el código ESCRIBE un solo vocabulario de nivel —
 * `gtr-go`/`gtr-fly`/`gtr-win`— y LEE los dos, siempre a través de
 * `normalizePackageLabel`. Los doc-ids, en cambio, siguen siendo aditivos: los
 * dos viven y los dos se escriben según lo que el cliente compre.
 */

/** `users/{u}/subscriptions/{id}` — la ACADEMIA (el paquete completo). */
export const SUB_ACADEMY_LEGACY = "trading-academy";
export const SUB_ACADEMY_NEW = "gtr-fly";

/** `users/{u}/subscriptions/{id}` — el LITE. */
export const SUB_LITE_LEGACY = "gtr-lite";
export const SUB_LITE_NEW = "gtr-go";

/**
 * El id de CONVENIENCIA: se compra, y el dispatcher lo PARTE en academia + CMMI.
 * Nunca queda un documento con este id colgando del usuario
 * (`Subscriptions/Functions.ts`).
 */
export const SUB_VIP_LEGACY = "gtr-vip";
export const SUB_VIP_NEW = "gtr-win";

/** El CMMI. No se renombra. */
export const SUB_MMI_COLOMBIA = "mmi-colombia";

/** Prefijo de los capítulos CMMI (`mmi-colombia`, `mmi-mexico`, …). */
export const MMI_SUB_PREFIX = "mmi-";

// =============================================================================
// LAS LICENCIAS
// =============================================================================
//
// Hasta 2026-07-24 había DOS, y estaban EMPAREJADAS con el paquete: quien compra
// academia recibe `distributor-license`; quien compra Lite recibe
// `lite-distributor-license`. El emparejamiento lo decide el switch de
// `getUserPackageStatus` (HelperFunctions) y sus espejos del front.
//
// Ahora se añade una TERCERA, la GLOBAL (`gtr-distributor-license`, «Licencia
// GTR Evolution»): vale para CUALQUIER paquete, y es la que se otorga en las
// activaciones nuevas.
//
// ⚠️ ES ADITIVO, IGUAL QUE EL RENOMBRE DE PAQUETES, Y POR LA MISMA RAZÓN: las
// licencias viejas siguen VIVAS y vigentes en los datos de todo el mundo. Nada
// pasa a mirar «sólo la global»: todo pasa a mirar «la suya O la global».
//
// ⚠️ EL EMPAREJAMIENTO NO SE BORRA, SE DEJA DORMIDO. El switch por paquete se
// queda en pie a propósito: después del go-live quieren volver a diferenciar
// (al `go` una licencia go, al `fly` una normal, al `vip` una anual). Si se
// borra hay que REESCRIBIRLO; dejándolo, basta con reactivarlo.

/** `users/{u}/subscriptions/{id}` — la licencia de la ACADEMIA. */
export const SUB_LICENSE_ACADEMY = "distributor-license";
/** `users/{u}/subscriptions/{id}` — la licencia del LITE. */
export const SUB_LICENSE_LITE = "lite-distributor-license";
/**
 * `users/{u}/subscriptions/{id}` — la licencia GLOBAL, «Licencia GTR Evolution».
 * Copia de `distributor-license` (365 días, $25) que vale para cualquier paquete.
 */
export const SUB_LICENSE_GLOBAL = "gtr-distributor-license";

/**
 * Las licencias que satisfacen el predicado de ACADEMIA (`isUserActive`, el
 * corte binario nocturno, el pay-walk del CMMI, la herramienta de uplines).
 *
 * Orden: la de siempre primero — es la que llevan los ~41.5k dormidos y toda la
 * historia, así que es la que hay que ver primero al depurar.
 *
 * ⚠️ `lite-distributor-license` NO está aquí, y no es un olvido: un Lite NUNCA
 * ha satisfecho `isUserActive`, y eso es producto, no accidente (`corte.ts`
 * :35-41 lo dice con todas las letras). Meterla aquí volvería activos a los
 * Lites de golpe. La GLOBAL sí entra porque es la copia de la de academia.
 */
export const ACADEMY_LICENSE_IDS: readonly string[] = [
  SUB_LICENSE_ACADEMY,
  SUB_LICENSE_GLOBAL,
];

/** Las licencias que valen para un LITE: la suya, o la global. */
export const LITE_LICENSE_IDS: readonly string[] = [
  SUB_LICENSE_LITE,
  SUB_LICENSE_GLOBAL,
];

/** Las TRES, para allowlists y validaciones (`KNOWN_SUBSCRIPTION_IDS`). */
export const ALL_LICENSE_IDS: readonly string[] = [
  SUB_LICENSE_ACADEMY,
  SUB_LICENSE_LITE,
  SUB_LICENSE_GLOBAL,
];

/** ¿Es una licencia que vale como la de ACADEMIA (la suya o la global)? */
export function isAcademyLicenseId(id: string | undefined): boolean {
  return id != undefined && ACADEMY_LICENSE_IDS.includes(id);
}

/** ¿Es una licencia que vale para un LITE (la suya o la global)? */
export function isLiteLicenseId(id: string | undefined): boolean {
  return id != undefined && LITE_LICENSE_IDS.includes(id);
}

/**
 * ¿Se puede RENOVAR en cualquier momento, sin esperar a los últimos 3 días?
 *
 * La exención existía sólo para `distributor-license` (`PaymentsFunc` :135) y la
 * GLOBAL LA HEREDA: es su copia. Sin eso se fabrican HUECOS de licencia —
 * quien intenta renovar pronto recibe un error, deja pasar los días y una noche
 * amanece sin licencia—, y cada noche de hueco el corte binario manda la
 * comisión a `lost`. Es dinero, y es mudo.
 *
 * El Lite NO la hereda: hoy tampoco la tiene, y ampliarla sería una decisión de
 * negocio que nadie ha tomado.
 */
export function isAnytimeRenewableSubscriptionId(
  id: string | undefined
): boolean {
  return isAcademyLicenseId(id);
}

/** Ambas generaciones del documento de ACADEMIA. Orden: legacy primero. */
export const ACADEMY_SUBSCRIPTION_IDS: readonly string[] = [
  SUB_ACADEMY_LEGACY,
  SUB_ACADEMY_NEW,
];

/** Ambas generaciones del documento LITE. Orden: legacy primero. */
export const LITE_SUBSCRIPTION_IDS: readonly string[] = [
  SUB_LITE_LEGACY,
  SUB_LITE_NEW,
];

/** Ambas generaciones del id de conveniencia VIP. */
export const VIP_SUBSCRIPTION_IDS: readonly string[] = [
  SUB_VIP_LEGACY,
  SUB_VIP_NEW,
];

// =============================================================================
// LEER UNA FAMILIA ENTERA — LOS TRES HELPERS DE LOS LECTORES
// =============================================================================
//
// EL FALLO QUE ESTOS TRES EXISTEN PARA IMPEDIR. El renombre es aditivo, así que
// un usuario TRADUCIDO acaba con DOS documentos de la misma familia: el viejo
// (`trading-academy`) VENCIDO y el nuevo (`gtr-fly`) VIGENTE. Quien lea sólo el
// viejo ve una fecha caducada y concluye lo contrario de la verdad: "no está
// activo". Y quien compra `gtr-fly` de cero no tiene documento viejo NINGUNO,
// así que quien pregunte "¿existe `trading-academy`?" lo toma por un usuario
// que estrena — y le abre la puerta del Premio, que es sólo para estrenos.
//
// Son DOS PREGUNTAS DISTINTAS y confundirlas es exactamente el fallo:
//   · ¿está VIGENTE?  → días restantes, `maxRemainingDaysOf`.
//   · ¿lo tuvo ALGUNA VEZ? → EXISTENCIA, `hasAnySubscriptionOf`. Un documento
//     vencido sigue existiendo, y por eso responde "sí" a la segunda y "no" a
//     la primera.
//
// Las dos miran LAS DOS GENERACIONES. Con un solo documento presente devuelven
// exactamente lo de siempre.

/**
 * La FAMILIA de doc-ids a la que pertenece un id: sus dos generaciones si las
 * tiene, o él solo si no es de ninguna familia conocida (`mmi-colombia`,
 * `prize`, `both`, un capítulo CMMI nuevo…).
 *
 * Es el mismo agrupamiento que usa `getUserSubscription` en el front, escrito
 * UNA vez: si mañana se añade una tercera generación, se añade aquí.
 */
export function subscriptionFamilyOf(id: string): readonly string[] {
  if (ACADEMY_SUBSCRIPTION_IDS.includes(id)) return ACADEMY_SUBSCRIPTION_IDS;
  if (LITE_SUBSCRIPTION_IDS.includes(id)) return LITE_SUBSCRIPTION_IDS;
  if (VIP_SUBSCRIPTION_IDS.includes(id)) return VIP_SUBSCRIPTION_IDS;
  if (ACADEMY_LICENSE_IDS.includes(id)) return ACADEMY_LICENSE_IDS;
  if (LITE_LICENSE_IDS.includes(id)) return LITE_LICENSE_IDS;
  return [id];
}

/**
 * El mapa `doc-id → días restantes` que viaja del backend al front.
 *
 * Lo construye `_getMemberSubscriptionsData` (`Unilevel/UnilevelFunc.ts`)
 * recorriendo `users/{u}/subscriptions` ENTERA, así que lleva TODOS los
 * documentos que la persona tenga —los dos de una familia traducida incluidos—
 * y el valor puede ser NEGATIVO cuando el documento está vencido. El front lo
 * rehidrata con `new Map(Object.entries(...))` antes de usarlo.
 */
export type RemainingDaysBySubscription = ReadonlyMap<string, number>;

/**
 * Los días restantes de una FAMILIA: los de la generación que más lejos llegue.
 *
 * ⚠️ Devuelve `undefined` cuando NO HAY NINGÚN documento de la familia, y eso no
 * es lo mismo que cero: `0` es "vence hoy" (vigente) y un `-40` es "venció hace
 * mes y medio" (existió). Quien colapse el `undefined` a `0` con un `?? 0` está
 * diciendo que un usuario que nunca compró está al día. Los llamantes que sólo
 * PINTAN un número sí pueden colapsarlo; los que DECIDEN, no.
 */
export function maxRemainingDaysOf(
  subscriptions: RemainingDaysBySubscription | undefined | null,
  ids: readonly string[]
): number | undefined {
  if (subscriptions == undefined || typeof subscriptions.get !== "function") {
    return undefined;
  }
  const found = ids
    .map((id) => subscriptions.get(id))
    .filter((d): d is number => typeof d === "number");
  return found.length > 0 ? Math.max(...found) : undefined;
}

/**
 * ¿Existe ALGÚN documento de la familia, vigente o vencido?
 *
 * Ésta es la pregunta de "¿ya estuvo activo alguna vez?" — la que decide si el
 * Premio de rifa se ofrece. Se mide por EXISTENCIA, nunca por vigencia.
 */
export function hasAnySubscriptionOf(
  subscriptions: RemainingDaysBySubscription | undefined | null,
  ids: readonly string[]
): boolean {
  if (subscriptions == undefined || typeof subscriptions.has !== "function") {
    return false;
  }
  return ids.some((id) => subscriptions.has(id));
}

// =============================================================================
// EL VOCABULARIO DE NIVEL — Y LA ÚNICA PUERTA POR LA QUE PASA
// =============================================================================
//
// El NIVEL (`SubscriptionPackage`) no es un doc-id: es una ETIQUETA que el código
// se inventa en `getUserPackageStatus` a partir de qué suscripciones están
// vigentes. Desde 2026-07-24 el vocabulario VIVO es `gtr-go` / `gtr-fly` /
// `gtr-win` / `none`, y NADA vuelve a escribir los tres nombres retirados.
//
// ⚠️ PERO EL DATO VIEJO SE QUEDA COMO ESTÁ. No hay backfill y no lo va a haber
// (Hector, 2026-07-24: «lo que ya pasó, ya pasó»). Hay etiquetas retiradas
// GUARDADAS en Firestore que siguen decidiendo accesos que la gente ya pagó:
//
//   · `schedule/{tipo}/{dia}/{hora}.{clase}.availableForPackage` — qué clases ve.
//   · `botsData/telegram/channels/{ch}.availableForPackage`      — qué canales ve.
//   · `botsData/telegram/channels/{ch}/users/{u}.currentPackage` — informativo.
//   · anclas tecleadas a mano en la consola de migración (`package`).
//
// Por eso ESTE FICHERO es el único del sistema donde los tres nombres retirados
// se escriben, y `normalizePackageLabel` es la única puerta por la que entran.
// Repartir la traducción por cada lector es exactamente cómo se pierde uno.

/** El LITE. Antes se llamaba de otra forma; hoy es esto y sólo esto. */
export const PKG_GO = "gtr-go";
/** La ACADEMIA sola. */
export const PKG_FLY = "gtr-fly";
/** Academia + CMMI. */
export const PKG_WIN = "gtr-win";
/** Sin paquete. */
export const PKG_NONE = "none";

/** El nivel de un usuario. El único vocabulario que el código ESCRIBE. */
export type SubscriptionPackage =
  | typeof PKG_GO
  | typeof PKG_FLY
  | typeof PKG_WIN
  | typeof PKG_NONE;

/** Las cuatro etiquetas vivas, para validar entradas y pintar mensajes. */
export const PACKAGE_LABELS: readonly SubscriptionPackage[] = [
  PKG_GO,
  PKG_FLY,
  PKG_WIN,
  PKG_NONE,
];

/**
 * 🔴 EL ÚNICO SITIO DEL SISTEMA DONDE SE ESCRIBEN LAS ETIQUETAS RETIRADAS.
 *
 * Si añades una traducción, añádela AQUÍ. Si ves uno de estos literales en
 * cualquier otro fichero, es un lector que se saltó la puerta.
 */
const RETIRED_PACKAGE_LABELS: Readonly<Record<string, SubscriptionPackage>> = {
  "gtr-lite": PKG_GO,
  "gtr-basic": PKG_FLY,
  "gtr-vip": PKG_WIN,
};

/**
 * Los doc-ids que NO son etiquetas de nivel bajo ningún concepto.
 *
 * Existe para que la guarda de abajo pueda distinguir "dato sucio" de "aquí
 * alguien pasó un documento donde iba un nivel", y son dos cosas muy distintas.
 * Ojo con lo que NO está en esta lista y no es un olvido: `gtr-lite`, `gtr-go`,
 * `gtr-fly` y `gtr-win` son doc-ids Y etiquetas a la vez, así que no se pueden
 * prohibir sin prohibir también el uso legítimo.
 */
const SUBSCRIPTION_ONLY_IDS: readonly string[] = [
  SUB_ACADEMY_LEGACY,
  SUB_MMI_COLOMBIA,
  ...ALL_LICENSE_IDS,
  "prize",
  "both",
];

/**
 * Un nivel GUARDADO (de cualquier generación) traducido al vocabulario vivo.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LO PRIMERO QUE HAY QUE ENTENDER: AQUÍ CONVIVEN DOS VOCABULARIOS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * El **doc-id de suscripción** es un documento que existe de verdad en
 * Firestore: `subscriptions/{id}` en el catálogo, y `users/{u}/subscriptions/{id}`
 * en la ficha de cada persona. Se puede abrir en la consola y tiene precio,
 * duración y puntos dentro.
 *
 * El **nivel** (`SubscriptionPackage`) no existe en ninguna parte hasta que el
 * código lo CALCULA. `getUserPackageStatus` mira qué suscripciones tiene una
 * persona vigentes y de ahí deduce una etiqueta: academia vigente y CMMI vigente
 * son `gtr-win`; academia sola es `gtr-fly`; sólo lite es `gtr-go`. Es un
 * resumen derivado, no un dato que alguien haya escrito a mano.
 *
 * El problema —y es la razón de que esta función tenga un guard— es que los dos
 * vocabularios COMPARTEN CADENAS. `gtr-go`, `gtr-fly` y `gtr-win` son a la vez
 * doc-ids del catálogo nuevo y etiquetas de nivel. Y `gtr-lite` es más
 * traicionero todavía: como doc-id está VIVO —es el que llevan los ~41,5k
 * durmientes y toda la historia—, y como etiqueta de nivel está RETIRADO. Así
 * que si alguien coge el doc-id `gtr-lite` de una suscripción y lo pasa por
 * aquí, esta función le devuelve `gtr-go` tan tranquila: acaba de convertir un
 * identificador de documento en una etiqueta de nivel, sin excepción y sin una
 * línea de log. TypeScript tampoco lo ve, porque `gtr-go` pertenece a las dos
 * uniones de tipos.
 *
 * Contra ese fallo hay lo que se puede tener: el guard de abajo, que se planta
 * cuando llega un doc-id que NO es también una etiqueta legítima
 * (`trading-academy` es el error natural, porque es el gemelo de `gtr-fly` en el
 * otro vocabulario). No cubre el caso de `gtr-lite`, porque ahí las dos lecturas
 * son cadenas idénticas y ninguna comprobación en tiempo de ejecución puede
 * separarlas — para eso harían falta tipos nominales, y eso es deuda anotada, no
 * algo que esté hecho. Si tocas esto, sepárelos de una vez.
 *
 * ⚠️ NO SE BORRA EL DOC-ID VIEJO, Y NO ES POR NOSTALGIA. Los ~41,5k durmientes
 * sólo tienen `users/{u}/subscriptions/gtr-lite` y `.../trading-academy`, y toda
 * la historia de BigQuery habla ese idioma. Quien venga a "limpiar" borrándolos
 * se lleva por delante la única forma de preguntar quién estaba activo en una
 * fecha pasada. Lo que murió es el NIVEL viejo, no el documento.
 *
 * ⚠️ EL PASADO NO SE NORMALIZA. Esta función es para ESTADO VIVO: el nivel de
 * hoy de una persona, la puerta de una clase, la tarjeta de su cuenta. Un
 * REGISTRO CERRADO —un leaderboard de marzo, un histórico de comisiones, un
 * `changeLog`— se enseña con la etiqueta con la que se guardó. Traducirlo haría
 * que aquel periodo se mostrara como «GTR Win», un nombre que no existía cuando
 * se corrió, y eso es reescribir el pasado en pantalla.
 *
 * ⚠️ FALLA CERRADO: devuelve `undefined` para lo que no reconoce, y quien
 * pregunta debe tratar ese `undefined` como "no", nunca como "sí". El precedente
 * es `Classes/RankGate.ts`: allí un id irresoluble colapsaba a prioridad 0 y la
 * clase salía DESBLOQUEADA para todo el mundo, `link` incluido. Un valor que no
 * se puede resolver no es "sin restricción": es una restricción que no podemos
 * demostrar satisfecha.
 */
export function normalizePackageLabel(
  value: string | undefined | null
): SubscriptionPackage | undefined {
  if (typeof value !== "string") return undefined;
  // Comparación EXACTA, sin `trim()`: `"gtr-fly "` con un espacio de más es un
  // dedazo en un JSON tecleado a mano, y limpiarlo en silencio es adivinar. El
  // criterio de este fichero es report, don't guess — y hay un spec que lo fija
  // (`packageRenameAdditive`), así que si lo ablandas, se pone rojo.
  const trimmed = value;

  // EL GUARD, y no lo quites por parecerte ruido: un doc-id que llega hasta aquí
  // es un cruce de vocabularios, es decir un BUG del código llamante, no un dato
  // sucio de un usuario. Los dos merecen tratos distintos — el dato sucio cae
  // abajo y devuelve `undefined` (cierra la puerta y sigue); el bug se planta,
  // porque si se tradujera en silencio decidiría accesos con una etiqueta
  // inventada y nadie se enteraría nunca.
  if (SUBSCRIPTION_ONLY_IDS.includes(trimmed) || trimmed.startsWith(MMI_SUB_PREFIX)) {
    throw new Error(
      `normalizePackageLabel: ${JSON.stringify(trimmed)} es un doc-id de ` +
        `SUSCRIPCIÓN, no una etiqueta de NIVEL. Son dos vocabularios distintos ` +
        `(ver la cabecera de esta función): el documento vive en Firestore, el ` +
        `nivel lo deriva "getUserPackageStatus". Los niveles válidos son ` +
        `${PACKAGE_LABELS.join(" | ")}. Si buscabas el nivel de esta familia, es ` +
        `"${PKG_FLY}" para la academia y "${PKG_GO}" para el lite — y ojo, esas ` +
        `cadenas TAMBIÉN son doc-ids, así que asegúrate de cuál de los dos ` +
        `necesitas antes de cambiar la llamada.`
    );
  }

  if ((PACKAGE_LABELS as readonly string[]).includes(trimmed)) {
    return trimmed as SubscriptionPackage;
  }
  return RETIRED_PACKAGE_LABELS[trimmed];
}

/**
 * NIVEL → la FAMILIA DE DOC-IDS donde viven de verdad sus días.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * UN NIVEL NO SIRVE COMO CLAVE DE DOCUMENTO, Y ES FÁCIL CREER QUE SÍ
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Dos de los tres niveles vivos se escriben igual que un doc-id del catálogo
 * (`gtr-go`, `gtr-fly`), así que usar la etiqueta de nivel para indexar el mapa
 * de suscripciones del usuario FUNCIONA casi siempre — y por eso se hizo, y por
 * eso nadie lo vio. Las excepciones son las que cuentan:
 *
 *   nivel      indexando por la etiqueta      qué pasa de verdad
 *   ---------  -----------------------------  -----------------------------
 *   `gtr-fly`  no hay doc `gtr-fly` en los     los ~41,5k de siempre tienen
 *              usuarios de siempre             sus días en `trading-academy`
 *   `gtr-go`   cuela por coincidencia          pero los de siempre están en
 *                                              `gtr-lite`
 *   `gtr-win`  **NUNCA hay documento**         el id de conveniencia se PARTE
 *                                              en academia + CMMI al activarse
 *                                              (`Subscriptions/Functions.ts`),
 *                                              así que no deja documento propio
 *
 * La fila de `gtr-win` es un fallo con años encima y ANTERIOR al renombre: antes
 * pasaba exactamente igual con `gtr-vip`. El caso especial escrito a mano se
 * hizo para el nivel de academia —que tampoco tenía documento homónimo— y al VIP
 * se le olvidó, así que la ficha de un socio VIP lleva enseñando CERO DÍAS desde
 * siempre. Los días de un WIN son los de su academia: es academia + CMMI.
 *
 * Por eso la traducción es explícita y tiene nombre. Un ternario que aprovecha
 * la coincidencia de cadenas es exactamente cómo se pierde uno entre los dos
 * vocabularios.
 *
 * ⚠️ FALLA CERRADO: `none` y cualquier cosa irreconocible devuelven una familia
 * VACÍA, y `maxRemainingDaysOf` sobre una lista vacía es `undefined` — "no
 * sabemos", que quien pregunta debe tratar como "no se ofrece nada".
 */
export function subscriptionFamilyForLevel(
  level: string | undefined | null
): readonly string[] {
  switch (normalizePackageLabel(level)) {
    // Un WIN es academia + CMMI: los días que gobiernan son los de su academia.
    case PKG_WIN:
    case PKG_FLY:
      return ACADEMY_SUBSCRIPTION_IDS;
    case PKG_GO:
      return LITE_SUBSCRIPTION_IDS;
    default:
      return [];
  }
}

/**
 * ¿Son el MISMO nivel, aunque estén escritos en generaciones distintas?
 * Falla cerrado: si alguno de los dos no se reconoce, la respuesta es `false`.
 */
export function packageLabelsMatch(
  a: string | undefined | null,
  b: string | undefined | null
): boolean {
  const left = normalizePackageLabel(a);
  const right = normalizePackageLabel(b);
  return left != undefined && left === right;
}

/**
 * ¿La lista de niveles permitidos incluye el nivel del usuario?
 *
 * Ésta es la función de las DECISIONES DE ACCESO (`availableForPackage` de las
 * clases y de los canales). Las dos partes se traducen, así que una config
 * guardada en el vocabulario retirado sigue dando acceso a quien ya lo tenía.
 *
 * ⚠️ FALLA CERRADO por los dos lados: si el nivel del usuario no se reconoce, no
 * hay acceso; una entrada irreconocible de la lista no abre nada.
 *
 * ⚠️ Y LAS ENTRADAS DE LA LISTA NO PASAN POR EL GUARD, a propósito. La lista es
 * configuración que edita una persona desde un formulario: puede tener un
 * `mmi-colombia` puesto de buena fe, o basura de hace tres años. Eso no es un
 * cruce de vocabularios en el código —que es lo que el guard existe para cazar—,
 * así que una entrada que no se entiende simplemente no abre nada, en vez de
 * tumbar la pantalla de horarios de todo el mundo.
 */
export function packageListIncludes(
  allowed: readonly string[] | undefined | null,
  pkg: string | undefined | null
): boolean {
  const wanted = normalizePackageLabel(pkg);
  if (wanted == undefined || !Array.isArray(allowed)) return false;
  return allowed.some((entry) => storedLevelOf(entry) === wanted);
}

/**
 * La traducción SIN el guard, para LISTAS DE CONFIGURACIÓN.
 *
 * Úsala —y sólo úsala— cuando recorras una lista que edita una persona
 * (`availableForPackage` de una clase o de un canal, `includedPackages` de un
 * leaderboard): ahí una entrada rara es un dedazo de alguien, no un cruce de
 * vocabularios en el código, y plantarse dejaría una pantalla entera en blanco
 * por una cadena que no entendemos. Para el nivel de una PERSONA usa siempre
 * `normalizePackageLabel`, que sí se planta.
 */
export function normalizeStoredPackageLabel(
  value: unknown
): SubscriptionPackage | undefined {
  return storedLevelOf(value);
}

function storedLevelOf(value: unknown): SubscriptionPackage | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value;
  if ((PACKAGE_LABELS as readonly string[]).includes(trimmed)) {
    return trimmed as SubscriptionPackage;
  }
  return RETIRED_PACKAGE_LABELS[trimmed];
}

/**
 * ¿Es una etiqueta de nivel escribible o leíble (viva o retirada)?
 *
 * Sin guard: la usan los validadores que quieren DAR UN BUEN MENSAJE con todos
 * los valores malos de golpe (`assertAnchorPackages`), y para eso necesitan
 * poder preguntar sin que la primera pregunta reviente.
 */
export function isPackageLabel(value: unknown): boolean {
  return storedLevelOf(value) != undefined;
}

/**
 * Las etiquetas de nivel, en TODAS sus generaciones, que significan `target`.
 *
 * Para los pocos sitios que no pueden llamar a `normalizePackageLabel` en la
 * comparación porque comparan en OTRO motor (un `IN UNNEST` de BigQuery, un
 * `where in` de Firestore). Fuera de esos, usa `packageListIncludes`.
 */
export function packageGenerationsOf(
  target: SubscriptionPackage
): readonly string[] {
  const retired = Object.keys(RETIRED_PACKAGE_LABELS).filter(
    (old) => RETIRED_PACKAGE_LABELS[old] === target
  );
  return [target, ...retired];
}

/** Etiquetas de PAQUETE (no doc-ids) para el lite, en las dos generaciones. */
export const LITE_PACKAGE_IDS: readonly string[] = packageGenerationsOf(PKG_GO);
/** Etiquetas de PAQUETE para la academia sola, en las dos generaciones. */
export const BASIC_PACKAGE_IDS: readonly string[] =
  packageGenerationsOf(PKG_FLY);
/** Etiquetas de PAQUETE para academia + CMMI, en las dos generaciones. */
export const VIP_PACKAGE_IDS: readonly string[] = packageGenerationsOf(PKG_WIN);

/**
 * Los paquetes que cobran comisión de upline / retienen puntos de rango:
 * academia sola o academia + CMMI, en las dos generaciones. Un Lite NO está.
 */
export const FULL_PACKAGE_IDS: readonly string[] = [
  ...BASIC_PACKAGE_IDS,
  ...VIP_PACKAGE_IDS,
];

export function isAcademySubscriptionId(id: string | undefined): boolean {
  return id != undefined && ACADEMY_SUBSCRIPTION_IDS.includes(id);
}

export function isLiteSubscriptionId(id: string | undefined): boolean {
  return id != undefined && LITE_SUBSCRIPTION_IDS.includes(id);
}

export function isVipSubscriptionId(id: string | undefined): boolean {
  return id != undefined && VIP_SUBSCRIPTION_IDS.includes(id);
}

/**
 * ¿Es una etiqueta de paquete que da derecho a comisión de academia?
 *
 * Es DINERO (`GtrLite.ts` la usa para decidir si el upline cobra), así que pasa
 * por la puerta: acepta las dos generaciones y niega lo que no reconoce.
 */
export function isFullPackageId(pkg: string | undefined): boolean {
  const label = normalizePackageLabel(pkg);
  return label === PKG_FLY || label === PKG_WIN;
}

/**
 * El id de ACADEMIA que hay que usar para una petición dada.
 *
 * Si la petición YA es de la familia academia (`trading-academy` o `gtr-fly`) se
 * respeta tal cual: es el que decide de qué documento de catálogo salen el
 * precio, los puntos y el `place`, y de qué `subscriptions/{id}` cuelga el
 * registro de idempotencia. Para todo lo demás (la vía de promociones, que entra
 * con `both`/`prize`) se conserva EXACTAMENTE el literal de siempre.
 */
export function academySubscriptionIdFor(requested: string | undefined): string {
  return isAcademySubscriptionId(requested)
    ? (requested as string)
    : SUB_ACADEMY_LEGACY;
}

/** El gemelo de `academySubscriptionIdFor` para el Lite. */
export function liteSubscriptionIdFor(requested: string | undefined): string {
  return isLiteSubscriptionId(requested)
    ? (requested as string)
    : SUB_LITE_LEGACY;
}

/**
 * El id de ACADEMIA de una activación SIN VOLUMEN — la herramienta manual de la
 * consola (`both` y `prize`).
 *
 * ⚠️ NO ES `academySubscriptionIdFor`, Y LA DIFERENCIA ES EL DEFAULT. Aquélla
 * cae en el id VIEJO porque cubre la vía de compra/promociones, donde el id que
 * no se reconoce es historia. Ésta cae en el NUEVO: la decisión de negocio está
 * tomada (Hector, 2026-07-25, «quedamos que era CAMBIAR esos paquetes») y la
 * herramienta regala el paquete nuevo, igual que lo hace una compra.
 *
 * 🔴 Y ES ADEMÁS LA CLAVE DEL CONTADOR DE PROMOCIONES, que es donde muerde. El
 * libro del patrocinador (`users/{u}/userIncentives/promotions`) está indexado
 * por DOC-ID, no por etiqueta, así que el saldo bajo `trading-academy` queda
 * INERTE y el que cuenta es el del id que se regala. Quien PREDIGA esa puerta
 * desde fuera —el pre-vuelo de `ActivateSubscriptionWithoutVolume`, que rellena
 * el saldo para que la activación de un admin no se caiga por contadores— tiene
 * que mirar EXACTAMENTE esta misma clave: leer una y decidir la otra es cómo el
 * pre-vuelo concluye «hay saldo» y la puerta responde «no hay», y la herramienta
 * deja de funcionar para todo el que lleve saldo del vocabulario viejo.
 *
 * ⚠️ TIENE UN GEMELO: `noVolumeAcademyId` en `Activations/GTRAcademy.ts` (la
 * puerta misma) calcula esto en línea. Son un solo artefacto escrito dos veces;
 * si tocas uno, el otro va detrás — o mejor, haz que aquél llame a esta función.
 */
export function noVolumeAcademySubscriptionId(
  requested: string | undefined
): string {
  return isAcademySubscriptionId(requested)
    ? (requested as string)
    : SUB_ACADEMY_NEW;
}
